import { useMemo, useState, useRef, useEffect, useCallback } from 'react'
import { errorHandler } from '../../../../services/util/error.service'
import AcompanhamentoPickingHttp from '../../../../services/api/https/dashboard/acompanhamento-picking.http'
import { getPeriodoMesAtual, parseToMysqlFormat } from '../../../../services/util/date.service'
import { useAcompanhamentoPickingContext } from '../../../../contexts/DashboardContext'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  FETCHING_DATA: 'Buscando dados dos pickings...',
}

export const ORDENAR_DEFAULT = { label: 'Data Criação - Mais recente', value: 'data_criacao_desc' }

const ORDENAR_OPTIONS = [
  { label: 'Data Criação - Mais recente', value: 'data_criacao_desc' },
  { label: 'Data Criação - Mais antigo', value: 'data_criacao_asc' },
  { label: 'Progresso - Crescente', value: 'progresso_asc' },
  { label: 'Progresso - Decrescente', value: 'progresso_desc' },
]

function getInitialState(acompanhamentoPickingContext = {}) {
  const periodo = acompanhamentoPickingContext.selectedPeriodo || getPeriodoMesAtual()

  return {
    pInicial: periodo.dataInicial,
    pFinal: periodo.dataFinal,
    loadingMessage: LOAD_MESSAGE.NONE,
    errorMessage: null,

    // Dados retornados da API
    dados: null, // { status, progresso, pickings }
    pickingsFiltrados: [],

    // Filtros
    searchText: acompanhamentoPickingContext.searchText || '',
    ordenarPor: acompanhamentoPickingContext.ordenarPor || ORDENAR_DEFAULT,
    empresasSelecionadas: acompanhamentoPickingContext.empresasSelecionadas || [],
  }
}

export default function useAcompanhamentoPicking(selectedTp) {
  const { acompanhamentoPickingContext, setAcompanhamentoPickingContext } =
    useAcompanhamentoPickingContext()
  const [state, setState] = useState(() => getInitialState(acompanhamentoPickingContext))
  const intervalRef = useRef(null)
  const prevSelectedTpRef = useRef(selectedTp)

  useEffect(() => {
    setAcompanhamentoPickingContext(prev => ({
      ...prev,
      selectedPeriodo: {
        dataInicial: state.pInicial,
        dataFinal: state.pFinal,
      },
      searchText: state.searchText,
      ordenarPor: state.ordenarPor,
      empresasSelecionadas: state.empresasSelecionadas,
    }))
  }, [
    setAcompanhamentoPickingContext,
    state.pInicial,
    state.pFinal,
    state.searchText,
    state.ordenarPor,
    state.empresasSelecionadas,
  ])

  const fetchData = useCallback(
    async (pInicial, pFinal) => {
      if (!selectedTp) {
        setState(prev => ({
          ...prev,
          errorMessage: 'Selecione um TP para buscar os dados',
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
        return
      }

      try {
        const data = await AcompanhamentoPickingHttp.getDashboard(
          selectedTp,
          parseToMysqlFormat(pInicial),
          parseToMysqlFormat(pFinal),
        )

        setState(prev => ({
          ...prev,
          dados: data,
          pickingsFiltrados: aplicarFiltrosEOrdenacao(
            data.pickings,
            prev.searchText,
            prev.ordenarPor,
            prev.empresasSelecionadas,
          ),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      } catch (e) {
        setState(prev => ({
          ...prev,
          errorMessage: errorHandler(e),
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      }
    },
    [selectedTp],
  )

  // Limpa intervalo ao desmontar
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [])

  // Detecta mudança de TP e reseta
  useEffect(() => {
    if (prevSelectedTpRef.current !== selectedTp) {
      prevSelectedTpRef.current = selectedTp
      setState(prev => ({
        ...prev,
        dados: null,
        pickingsFiltrados: [],
      }))
    }
  }, [selectedTp])

  // Busca automática ao entrar na tela se TP já está selecionado
  useEffect(() => {
    if (!selectedTp || state.dados !== null) return
    setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.FETCHING_DATA }))
    fetchData(state.pInicial, state.pFinal)
  }, [selectedTp, fetchData, state.dados, state.pInicial, state.pFinal])

  const localDispatch = useMemo(() => {
    return {
      resetState() {
        setState(prev => ({
          ...prev,
          errorMessage: null,
          loadingMessage: LOAD_MESSAGE.NONE,
        }))
      },

      onPeriodoInicialChange(pInicial) {
        setState(prev => ({ ...prev, pInicial }))
      },

      onPeriodoFinalChange(pFinal) {
        setState(prev => ({ ...prev, pFinal }))
      },

      async onBtnPesquisar(pInicial, pFinal) {
        if (!selectedTp) {
          setState(prev => ({
            ...prev,
            pInicial,
            pFinal,
            errorMessage: 'Selecione um TP para buscar os dados',
          }))
          return
        }

        setState(prev => ({
          ...prev,
          pInicial,
          pFinal,
          loadingMessage: LOAD_MESSAGE.FETCHING_DATA,
        }))
        await fetchData(pInicial, pFinal)

        // Auto refresh a cada 3 minutos
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
        }
        intervalRef.current = setInterval(
          () => {
            fetchData(pInicial, pFinal)
          },
          3 * 60 * 1000,
        )
      },

      stopAutoRefresh() {
        if (intervalRef.current) {
          clearInterval(intervalRef.current)
          intervalRef.current = null
        }
      },

      onSearchTextChange(text) {
        setState(prev => {
          const pickingsFiltrados = aplicarFiltrosEOrdenacao(
            prev.dados ? prev.dados.pickings : [],
            text,
            prev.ordenarPor,
            prev.empresasSelecionadas,
          )
          return { ...prev, searchText: text, pickingsFiltrados }
        })
      },

      onOrdenarPorChange(option) {
        setState(prev => {
          const pickingsFiltrados = aplicarFiltrosEOrdenacao(
            prev.dados ? prev.dados.pickings : [],
            prev.searchText,
            option,
            prev.empresasSelecionadas,
          )
          return { ...prev, ordenarPor: option, pickingsFiltrados }
        })
      },

      onEmpresasSelecionadasChange(empresas) {
        setState(prev => {
          const pickingsFiltrados = aplicarFiltrosEOrdenacao(
            prev.dados ? prev.dados.pickings : [],
            prev.searchText,
            prev.ordenarPor,
            empresas,
          )
          return { ...prev, empresasSelecionadas: empresas, pickingsFiltrados }
        })
      },

      onLimparFiltros() {
        setState(prev => {
          const pickingsFiltrados = aplicarFiltrosEOrdenacao(
            prev.dados ? prev.dados.pickings : [],
            '',
            ORDENAR_DEFAULT,
            [],
          )
          return {
            ...prev,
            searchText: '',
            ordenarPor: ORDENAR_DEFAULT,
            empresasSelecionadas: [],
            pickingsFiltrados,
          }
        })
      },
    }
  }, [selectedTp, fetchData])

  return { state, localDispatch, ORDENAR_OPTIONS }
}

function aplicarFiltrosEOrdenacao(pickings, searchText, ordenarPor, empresasSelecionadas) {
  let resultado = [...(pickings || [])]

  // Filtra por empresas
  if (empresasSelecionadas && empresasSelecionadas.length > 0) {
    const empresasSet = new Set(
      empresasSelecionadas
        .map(e => {
          if (typeof e === 'string') return e
          if (e && typeof e === 'object') return e.value || e.label || ''
          return ''
        })
        .map(e => String(e).trim().toLowerCase())
        .filter(Boolean),
    )
    resultado = resultado.filter(p =>
      empresasSet.has(
        String(p.company_name || '')
          .trim()
          .toLowerCase(),
      ),
    )
  }

  // Filtra por texto
  if (searchText && searchText.trim()) {
    const textLower = searchText.toLowerCase().trim()
    resultado = resultado.filter(
      p =>
        String(p.id).includes(textLower) ||
        (p.name || '').toLowerCase().includes(textLower) ||
        (p.company_name || '').toLowerCase().includes(textLower) ||
        (p.created_at || '').toLowerCase().includes(textLower) ||
        (p.status || '').toLowerCase().includes(textLower),
    )
  }

  // Ordenação
  const ordenarValue = ordenarPor ? ordenarPor.value : ORDENAR_DEFAULT.value
  switch (ordenarValue) {
    case 'data_criacao_desc':
      resultado.sort((a, b) => new Date(b.created_at_raw) - new Date(a.created_at_raw))
      break
    case 'data_criacao_asc':
      resultado.sort((a, b) => new Date(a.created_at_raw) - new Date(b.created_at_raw))
      break
    case 'progresso_asc':
      resultado.sort((a, b) => (a.percentual_separacao || 0) - (b.percentual_separacao || 0))
      break
    case 'progresso_desc':
      resultado.sort((a, b) => (b.percentual_separacao || 0) - (a.percentual_separacao || 0))
      break
    default:
      resultado.sort((a, b) => new Date(b.created_at_raw) - new Date(a.created_at_raw))
  }

  return resultado
}
